import {
	useEffect,
	useRef,
	useState,
	type ReactElement,
	type ReactNode,
} from 'react';
import { Link, NavLink, useLocation } from 'react-router';

import { parseClassnames } from '@incloodsolutions/react-toolkit';

import { type UserPermission } from '@workinance/shared/data/permissions';
import { BrandWordmarkComponent } from '@workinance/shared/ui/BrandWordmarkComponent';
import { CardLayoutComponent } from '@workinance/shared/ui/CardLayoutComponent';
import {
	ChevronDownIcon,
	MenuIcon,
	type IIconProps,
} from '@workinance/shared/ui/icons';
import { ModalLayoutComponent } from '@workinance/shared/ui/ModalLayoutComponent';

export interface INavigationItem {
	label: string;
	/** Omit for a parent whose only job is grouping its `children` -- it renders as a cluster header, not a link. */
	to?: string;
	icon?: (props: IIconProps) => ReactElement;
	/** Shown to owners regardless, and to anyone else who has at least one of these. Omit for everyone. */
	requiredPermissions?: UserPermission[];
	/** Pages grouped under this one label in the side navigation, such as Customers and Surveys under CRM. */
	children?: INavigationItem[];
}

/** The top bar renders every item as a pill link, so (unlike the side navigation) it has no grouping -- `to` and `icon` stay required here. */
export type IFlatNavigationItem = INavigationItem &
	Required<Pick<INavigationItem, 'to' | 'icon'>>;

interface IAppShellLayoutComponentProps {
	/** Where the wordmark links to inside the app. */
	homePath: string;
	/** Pages shown as pills in the top bar and as tabs on phones. Keep to five or fewer. */
	primaryNavigation: IFlatNavigationItem[];
	/** Less used pages, tucked under "More". */
	secondaryNavigation?: IFlatNavigationItem[];
	/** The account menu, shown at the right of the top bar. */
	accountMenu: ReactNode;
	/** Stretch the top bar and the page to the whole window instead of a centred column, for screens like a POS. */
	isFullWidth?: boolean;
	children: ReactNode;
}

const contentWidthClasses =
	'mx-auto w-full max-w-7xl px-[clamp(16px,4vw,48px)]';
const fullWidthClasses = 'w-full px-4 sm:px-6';

/**
 * The frame around every signed-in page in every app: a slim top bar on desktop with the navigation
 * as pills, and a bottom tab bar on phones and tablets so the main pages are always one thumb away.
 */
export function AppShellLayoutComponent({
	homePath,
	primaryNavigation,
	secondaryNavigation = [],
	accountMenu,
	isFullWidth = false,
	children,
}: IAppShellLayoutComponentProps) {
	const widthClasses = isFullWidth ? fullWidthClasses : contentWidthClasses;
	const location = useLocation();
	const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
	const [isMoreSheetOpen, setIsMoreSheetOpen] = useState(false);
	const moreMenuRef = useRef<HTMLDivElement>(null);
	const hasSecondaryNavigation = secondaryNavigation.length > 0;
	const isSecondaryActive = secondaryNavigation.some((item) =>
		location.pathname.startsWith(item.to),
	);

	const closeMoreMenus = () => {
		setIsMoreMenuOpen(false);
		setIsMoreSheetOpen(false);
	};

	useEffect(() => {
		if (!isMoreMenuOpen) return;

		function handleClickOutside(event: MouseEvent) {
			if (
				moreMenuRef.current &&
				!moreMenuRef.current.contains(event.target as Node)
			) {
				setIsMoreMenuOpen(false);
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isMoreMenuOpen]);

	return (
		<div className="min-h-dvh bg-paper font-grotesk text-ink">
			<header className="sticky top-0 z-30 border-b border-rule bg-paper/85 backdrop-blur">
				<div
					className={parseClassnames(
						widthClasses,
						'flex h-16 items-center gap-3 md:gap-4',
					)}
				>
					<Link
						to={homePath}
						aria-label="Workinance home"
						className="no-underline"
					>
						{/* On a phone the bottom tab bar carries the navigation, so a short mark leaves the bar room. */}
						<BrandWordmarkComponent hasShortMarkOnMobile />
					</Link>

					<nav
						aria-label="Main"
						className="mx-auto hidden min-w-0 items-center gap-1 lg:flex"
					>
						{primaryNavigation.map(({ label, to, icon: Icon }) => (
							<NavLink
								key={to}
								to={to}
								className={({ isActive }) =>
									parseClassnames(
										'inline-flex items-center gap-2 rounded-full px-4 py-2 text-[15px] leading-none font-semibold no-underline transition-colors',
										{
											'bg-ink text-paper': isActive,
											'text-mute hover:bg-brand-soft hover:text-ink': !isActive,
										},
									)
								}
							>
								<Icon className="size-4" /> {label}
							</NavLink>
						))}

						{hasSecondaryNavigation && (
							<div ref={moreMenuRef} className="relative">
								<button
									type="button"
									aria-expanded={isMoreMenuOpen}
									onClick={() => setIsMoreMenuOpen((isOpen) => !isOpen)}
									className={parseClassnames(
										'inline-flex cursor-pointer items-center gap-2 rounded-full px-4 py-2 text-[15px] leading-none font-semibold transition-colors',
										{
											'bg-ink text-paper': isSecondaryActive,
											'text-mute hover:bg-brand-soft hover:text-ink':
												!isSecondaryActive,
										},
									)}
								>
									More <ChevronDownIcon className="size-4" />
								</button>
								{isMoreMenuOpen && (
									<CardLayoutComponent className="absolute top-full left-0 z-50 mt-2 min-w-60 rounded-3xl p-2 shadow-xl">
										{secondaryNavigation.map(({ label, to, icon: Icon }) => (
											<NavLink
												key={to}
												to={to}
												onClick={closeMoreMenus}
												className={({ isActive }) =>
													parseClassnames(
														'flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium no-underline transition-colors hover:bg-brand-soft',
														{ 'bg-brand-soft text-brand': isActive },
													)
												}
											>
												<Icon className="size-4 text-mute" /> {label}
											</NavLink>
										))}
									</CardLayoutComponent>
								)}
							</div>
						)}
					</nav>

					<div className="ml-auto flex items-center gap-1 sm:gap-2">
						{accountMenu}
					</div>
				</div>
			</header>

			<main
				className={parseClassnames(widthClasses, {
					'pt-8 pb-32 md:pt-12 lg:pb-20': !isFullWidth,
					// Tight to the top bar and the window edge, so a full-height page like the POS fills the screen.
					'pt-4 pb-32 lg:pb-4': isFullWidth,
				})}
			>
				{children}
			</main>

			<nav
				aria-label="Main"
				className="fixed inset-x-0 bottom-0 z-30 border-t border-rule bg-panel/90 px-2 pt-2 pb-[max(8px,env(safe-area-inset-bottom))] backdrop-blur lg:hidden"
			>
				<ul className="mx-auto flex max-w-md justify-between">
					{primaryNavigation.map(({ label, to, icon: Icon }) => (
						<li key={to} className="flex-1">
							<NavLink
								to={to}
								className={({ isActive }) =>
									parseClassnames(
										'mx-auto flex w-16 flex-col items-center gap-1 rounded-2xl py-1.5 text-[11px] leading-none font-semibold no-underline transition-colors',
										{
											'bg-brand-soft text-brand': isActive,
											'text-mute': !isActive,
										},
									)
								}
							>
								<Icon className="size-5" /> {label}
							</NavLink>
						</li>
					))}
					{hasSecondaryNavigation && (
						<li className="flex-1">
							<button
								type="button"
								onClick={() => setIsMoreSheetOpen(true)}
								className={parseClassnames(
									'mx-auto flex w-16 cursor-pointer flex-col items-center gap-1 rounded-2xl py-1.5 text-[11px] leading-none font-semibold transition-colors',
									{
										'bg-brand-soft text-brand': isSecondaryActive,
										'text-mute': !isSecondaryActive,
									},
								)}
							>
								<MenuIcon className="size-5" /> More
							</button>
						</li>
					)}
				</ul>
			</nav>

			{hasSecondaryNavigation && (
				<ModalLayoutComponent
					isOpen={isMoreSheetOpen}
					onClose={() => setIsMoreSheetOpen(false)}
					title="More"
					size="sm"
				>
					<div className="grid grid-cols-2 gap-3">
						{secondaryNavigation.map(({ label, to, icon: Icon }) => (
							<NavLink
								key={to}
								to={to}
								onClick={closeMoreMenus}
								className={({ isActive }) =>
									parseClassnames(
										'flex flex-col items-center gap-2 rounded-2xl border px-2 py-4 text-center text-sm font-semibold no-underline transition-colors',
										{
											'border-brand bg-brand-soft text-brand': isActive,
											'border-rule text-mute hover:bg-brand-soft': !isActive,
										},
									)
								}
							>
								<Icon className="size-5" /> {label}
							</NavLink>
						))}
					</div>
				</ModalLayoutComponent>
			)}
		</div>
	);
}
